import { defineStore } from 'pinia'
import { computed } from 'vue'
import { createSyncedRows } from '../services/syncedRows'
import {
  exceptionId,
  followSeries,
  makeException,
  parseRepeat,
  seriesFromEdit,
  splitSeries,
  type EventRepeat,
  type RowChanges,
} from '../utils/calendarRepeat'

// Calendar events sync one row per event (see syncedRows), so edits to
// different events on different devices never overwrite each other. A
// repeating event is one row with a rule; a changed or skipped occurrence is
// a row of its own (see utils/calendarRepeat).

export const CALENDAR_COLORS = ['accent', 'blue', 'success', 'warning', 'error', 'muted'] as const
export type CalendarColor = typeof CALENDAR_COLORS[number]

export interface CalendarEvent {
  id: string
  title: string
  /** 'YYYY-MM-DD' for all-day events, otherwise floating local 'YYYY-MM-DDTHH:mm'. */
  start: string
  /** Exclusive end in the same form as `start`, or null for a default length. */
  end: string | null
  allDay: boolean
  color: CalendarColor
  notes: string
  /** The page, folder, or desk the event is about, or null. */
  linkedId: string | null
  /** How a series repeats; null for a single event and for an exception. */
  repeat: EventRepeat | null
  /** On an exception: the series whose occurrence it replaces. */
  seriesId: string | null
  /** On an exception: the day, 'YYYY-MM-DD', the series planned that occurrence for. */
  occurrence: string | null
  /** On an exception: the occurrence is skipped. */
  canceled: boolean
  /** Time set aside for writing; sprints run during it show on it afterwards. */
  writing: boolean
  /** A Docket task to do before it (before each occurrence, when it repeats); see utils/prepTasks. */
  prep: EventPrep | null
}

interface EventPrep {
  title: string
  /** Days before the event the task is planned for; 0 is the day itself. */
  leadDays: number
}

function parsePrep(raw: any): EventPrep | null {
  if (!raw || typeof raw.title !== 'string' || !raw.title.trim()) return null
  const lead = Math.round(Number(raw.leadDays))
  return { title: raw.title.trim().slice(0, 500), leadDays: Number.isFinite(lead) ? Math.min(60, Math.max(0, lead)) : 1 }
}

function parseEvent(raw: any): CalendarEvent | null {
  if (!raw
    || typeof raw.id !== 'string'
    || typeof raw.title !== 'string'
    || typeof raw.start !== 'string'
    || typeof raw.allDay !== 'boolean') return null
  return normalizeEvent(raw)
}

function normalizeEvent(event: CalendarEvent): CalendarEvent {
  const isException = typeof event.seriesId === 'string'
    && typeof event.occurrence === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(event.occurrence)
  return {
    id: event.id,
    title: event.title,
    start: event.start,
    end: event.end ?? null,
    allDay: event.allDay,
    color: CALENDAR_COLORS.includes(event.color) ? event.color : 'accent',
    notes: typeof event.notes === 'string' ? event.notes : '',
    linkedId: typeof event.linkedId === 'string' ? event.linkedId : null,
    writing: event.writing === true,
    // A prep task belongs to the whole series, never to one occurrence.
    prep: isException ? null : parsePrep(event.prep),
    ...(isException
      ? { repeat: null, seriesId: event.seriesId, occurrence: event.occurrence, canceled: event.canceled === true }
      : { repeat: parseRepeat(event.repeat, event.start), seriesId: null, occurrence: null, canceled: false }),
  }
}

export const useCalendarStore = defineStore('calendar', () => {
  const rows = createSyncedRows<CalendarEvent>({
    storageKey: 'ink-calendar',
    remotePrefix: 'calendar.event.',
    parse: parseEvent,
  })

  const events = computed(() => Object.values(rows.values.value))

  function findEvent(id: string): CalendarEvent | undefined {
    return rows.values.value[id]
  }

  function saveEvent(event: CalendarEvent) {
    rows.write(event.id, normalizeEvent(event))
  }

  /** Deletes an event; deleting a series takes its exceptions with it. */
  function deleteEvent(id: string) {
    if (rows.values.value[id]) rows.write(id, null)
    for (const exception of exceptionsOf(id)) rows.write(exception.id, null)
  }

  function exceptionsOf(seriesId: string): CalendarEvent[] {
    return events.value.filter((event) => event.seriesId === seriesId)
  }

  function apply(changes: RowChanges) {
    for (const id of changes.remove) rows.write(id, null)
    for (const event of changes.save) rows.write(event.id, normalizeEvent(event))
  }

  /** "Only this event": the occurrence on `occurrence` becomes `changed`. */
  function saveOccurrence(seriesId: string, occurrence: string, changed: CalendarEvent) {
    const series = rows.values.value[seriesId]
    if (series) rows.write(exceptionId(seriesId, occurrence), normalizeEvent(makeException(series, occurrence, changed)))
  }

  /** Skips one occurrence. */
  function skipOccurrence(seriesId: string, occurrence: string) {
    const series = rows.values.value[seriesId]
    if (!series) return
    const current = rows.values.value[exceptionId(seriesId, occurrence)] ?? series
    rows.write(exceptionId(seriesId, occurrence), normalizeEvent(makeException(series, occurrence, current, true)))
  }

  /** Puts an occurrence back as the series has it, undoing a change or a skip. */
  function restoreOccurrence(seriesId: string, occurrence: string) {
    const id = exceptionId(seriesId, occurrence)
    if (rows.values.value[id]) rows.write(id, null)
  }

  /** "All events": one occurrence was edited; the series and its exceptions follow. */
  function saveAll(seriesId: string, occurrence: string, edited: CalendarEvent) {
    const before = rows.values.value[seriesId]
    if (!before) return
    const after = normalizeEvent(seriesFromEdit(before, occurrence, edited))
    rows.write(seriesId, after)
    apply(followSeries(before, after, exceptionsOf(seriesId)))
  }

  /**
   * "This and following": the series stops before `occurrence`, and a new
   * series made from the edited occurrence carries on from there.
   */
  function saveFollowing(seriesId: string, occurrence: string, edited: CalendarEvent) {
    const series = rows.values.value[seriesId]
    if (!series?.repeat) return
    const { ended, next, changes } = splitSeries(series, occurrence, createId(), exceptionsOf(seriesId))
    rows.write(seriesId, ended ? normalizeEvent(ended) : null)
    rows.write(next.id, normalizeEvent(next))
    apply(changes)
    // An unchanged rule is the new series' own, with what's left of an "after N times".
    const sameRule = JSON.stringify(edited.repeat) === JSON.stringify(series.repeat)
    saveAll(next.id, occurrence, { ...edited, id: next.id, repeat: sameRule ? next.repeat : edited.repeat })
  }

  /** "This and following" for a delete: the series stops before `occurrence`. */
  function deleteFollowing(seriesId: string, occurrence: string) {
    const series = rows.values.value[seriesId]
    if (!series?.repeat) return
    const { ended, changes } = splitSeries(series, occurrence, createId(), exceptionsOf(seriesId))
    if (!ended) {
      deleteEvent(seriesId)
      return
    }
    rows.write(seriesId, normalizeEvent(ended))
    apply({ save: [], remove: changes.remove })
  }

  function createId(): string {
    return crypto.randomUUID()
  }

  return {
    events,
    findEvent,
    saveEvent,
    deleteEvent,
    exceptionsOf,
    saveOccurrence,
    skipOccurrence,
    restoreOccurrence,
    saveAll,
    saveFollowing,
    deleteFollowing,
    createId,
    syncWithRemote: rows.syncWithRemote,
  }
})
